import React, {createContext, useContext} from 'react';
import {assert} from 'assert-ts';
import type {ThesaurusId} from 'domainTypes';
import type {CodeListMap} from 'schemaDefinition/types';
import type {CodeListId} from 'services/codelists/types';
import type {
  FilterModel,
  PaginationModel,
  Sorting,
} from 'services/settings/types';
import type {ThesaurusesMap} from 'services/thesaurus/types';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import type {ColumnConfig} from '../table/types';

/**
 * Persisted per-schema table configuration (column layout, sorting, filtering,
 * pagination) together with setters. The host owns the persistence; the schema
 * table layer reads and updates it through this seam.
 */
export type SchemaTableConfig = {
  columnConfigs: ColumnConfig[] | undefined;
  sortingConfig?: Sorting;
  filterModel?: FilterModel;
  paginationModel?: PaginationModel;
  setColumnConfigs: (columnConfigs: ColumnConfig[]) => void;
  setSortingConfig: (sorting?: Sorting) => void;
  setFilterModel: (filterModel?: FilterModel) => void;
  setPaginationModel: (paginationModel: PaginationModel) => void;
  resetPaginationModel: (pageSize: number) => void;
};

/**
 * Data-access seam between the schema rendering layer and the host application.
 *
 * The schema layer needs codelists and thesauruses that the host owns in its
 * (Redux) store. Rather than reaching into the store directly, it consumes these
 * accessor hooks via context, so the host supplies the concrete implementation.
 * This keeps the schema layer decoupled from app state management.
 */
export type SchemaDataAccess = {
  /** Resolve a set of codelist ids to a map of loaded codelists. */
  useCodelists: (ids: (CodeListId | LinkedRoleCodeListId)[]) => CodeListMap;
  /** Resolve a set of thesaurus ids to a map of loaded thesauruses. */
  useThesauruses: (ids: ThesaurusId[]) => ThesaurusesMap;
  /** Read + update the persisted table configuration for a schema. */
  useTableConfig: (schemaName: string) => SchemaTableConfig;
};

const SchemaDataContext = createContext<SchemaDataAccess | undefined>(
  undefined,
);

export const SchemaDataProvider: React.FC<
  React.PropsWithChildren<{value: SchemaDataAccess}>
> = ({value, children}) => (
  <SchemaDataContext.Provider value={value}>
    {children}
  </SchemaDataContext.Provider>
);

export const useSchemaDataAccess = (): SchemaDataAccess => {
  return assert(
    useContext(SchemaDataContext),
    'useSchemaDataAccess: missing SchemaDataProvider. The host application must ' +
      'provide a SchemaDataAccess implementation.',
  );
};
